import { Component, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DialogCloseReason, SuiDialogBase } from '@sui/dialog';
import { TranslateService } from '@ngx-translate/core';

import { ScheduleEditorComponent } from '../../schedule-editor/schedule-editor.component';
import {
  EngineSchedule,
  RecurrenceDefinition,
} from 'src/app/core/models/schedule.models';

export interface ScheduleEditDialogData {
  schedule: EngineSchedule;
}

export interface ScheduleEditDialogOutput {
  title: string;
  recurrence: RecurrenceDefinition;
  timezone_id: number | null;
  misfire_policy_id: number;
  allow_overlap: boolean;
}

/**
 * Admin edit dialog for an engine schedule: title + the full recurrence editor
 * (engine mode - recurrence types, timezone, misfire policy, overlap). The grid preserves
 * the schedule's action type/params and merges this output into the update payload.
 */
@Component({
  selector: 'app-schedule-edit-dialog',
  standalone: false,
  templateUrl: './schedule-edit-dialog.component.html',
})
export class ScheduleEditDialogComponent extends SuiDialogBase<
  ScheduleEditDialogData,
  ScheduleEditDialogOutput
> {
  private translateService = inject(TranslateService);

  @ViewChild(ScheduleEditorComponent) editor!: ScheduleEditorComponent;

  form = new FormGroup({
    title: new FormControl<string>('', [Validators.required]),
  });

  get schedule(): EngineSchedule {
    return this.dialogData.schedule;
  }

  get dialogTitle(): string {
    return this.translateService.instant('schedules-grid.dialog.edit-schedule');
  }

  ngOnInit(): void {
    this.form.get('title')?.setValue(this.schedule.title ?? '');
  }

  cancel(): void {
    this.close({ reason: DialogCloseReason.Cancel });
  }

  save(): void {
    if (this.form.invalid || !this.editor?.formGroup?.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const editorValue = this.editor.formGroup.getRawValue();

    this.close({
      reason: DialogCloseReason.Confirm,
      data: {
        title: this.form.get('title')?.value ?? '',
        recurrence: this.editor.toRecurrenceDefinition(),
        timezone_id:
          editorValue.timezone_id != null
            ? Number(editorValue.timezone_id)
            : null,
        misfire_policy_id: Number(editorValue.misfire_policy_id) || 1,
        allow_overlap: !!editorValue.allow_overlap,
      },
    });
  }
}
